import React, { useState, useCallback, useRef, useEffect } from 'react';
import { convertBase64 } from '../../../..';
import Icon from '../../DataDisplay/Icon/Icon';
import Button from '../Button/Button';
import ErrorText from '../../Text/ErrorText/ErrorText';
import UploadFileDialogue from './Dialogue/UploadFileDialogue';
import styles from './UploadFile.module.scss';
import { withStyles } from '../../../utils';
import { useTranslation, Trans } from 'react-i18next';

const cn = withStyles(styles);

export type UploadFileProps<T> = {
  id: string;
  idFileName?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'onboarding';
  height?: string;
  name: T;
  maxLength?: number;
  validExtensions?: string[];
  accept?: string;
  value?: string;
  valueFileName?: string;
  disabled?: boolean;
  error?: string | null;
  companyName?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  base64?: boolean;
  base64FileName?: string;
  maxMBSize?: number;
  deleteButton?: boolean;
  language?: string;
  shouldReset?: boolean;
  keepExtension?: boolean;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  onDeleteClick?: () => void;
  onChangeFileName?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlurFileName?: (event: React.FocusEvent<HTMLInputElement>) => void;
  handleFile?: (event: boolean) => void;
  handleValidFile?: (event: boolean) => void;
  handleFileName?: (event: string) => void;
  handleHasNewFile?: (event: boolean) => void;
  handleNewFile?: (event: string) => void;
};

const UploadFile = <T extends string>(props: UploadFileProps<T>): React.JSX.Element => {
  const {
    idFileName = '',
    variant = '',
    height = '88px',
    name,
    maxLength,
    validExtensions,
    accept,
    value,
    valueFileName,
    disabled = false,
    error = null,
    companyName,
    buttonBackgroundColor,
    buttonTextColor = 'black',
    base64 = false,
    base64FileName = '',
    maxMBSize,
    deleteButton = false,
    language = 'en',
    shouldReset,
    keepExtension = false,
    onBlur,
    onChange = () => {},
    onDeleteClick = () => {},
    onChangeFileName = () => {},
    onBlurFileName = () => {},
    handleFile = () => {},
    handleValidFile = () => {},
    handleFileName = () => {},
    handleHasNewFile = () => {},
    handleNewFile = () => {},
  } = props;

  const { t } = useTranslation('components', { keyPrefix: 'UploadFile' });
  const [base64File, setBase64File] = useState(value || '');
  const [url, setUrl] = useState('');
  const [fileName, setFileName] = useState(valueFileName || '');
  const [hovering, setHovering] = useState(false);
  const [isFileVaild, setIsFileValid] = useState(false);
  const [shouldDeleteFile, setShouldDeleteFile] = useState(false);
  const [shouldDownloadFile, setShouldDownloadFile] = useState(false);
  const [invalidFileMessage, setInvalidFileMessage] = useState('');

  // Helper function to get filename based on keepExtension prop
  const getProcessedFileName = (fileName: string): string => {
    return keepExtension ? fileName : fileName.split('.')[0];
  };

  useEffect(() => {
    if (
      value?.startsWith('data:application/pdf;base64,') ||
      value?.startsWith('JVB') ||
      value?.startsWith('/9j/') ||
      value?.startsWith('iVB')
    ) {
      setBase64File(value || '');
    } else {
      setUrl(value || '');
    }
  }, [value]);

  // If the document value is a URL, the button allows downloading it. If a new base64 is updated, the button allows viewing it.
  useEffect(() => {
    if (url !== '') {
      setShouldDownloadFile(true);
      if (url !== base64File) {
        setShouldDownloadFile(false);
      }
    } else {
      setShouldDownloadFile(false);
    }
  }, [url, base64File]);

  function setError(errorType: 'maxLimit' | 'fileType' | 'maxFileName' | null) {
    if (errorType) {
      const msg = t(`Error.${errorType}`, {
        size: maxMBSize || 0,
        extensions: validExtensions?.join(', ').toUpperCase() ?? 'PDF, PNG or JPG',
      });

      setInvalidFileMessage(msg);
    }
  }

  useEffect(() => {
    if (value !== '' && valueFileName !== '') {
      handleValidFile(true);
      setIsFileValid(true);
      setBase64File(value || '');
      setFileName(valueFileName || '');
    } else {
      setIsFileValid(false);
      setBase64File('');
      setFileName('');
    }
  }, [value, valueFileName]);

  // When moving to the next page, reset the document
  useEffect(() => {
    if (shouldReset) {
      setInvalidFileMessage('');
      setError(null);
      if (value === '' || value === null || value === undefined) {
        setFileName('');
        setBase64File('');
      }
    }
  }, [shouldReset]);

  useEffect(() => {
    if (base64FileName !== '') {
      setFileName(base64FileName);
    }
  }, [base64FileName]);

  useEffect(() => {
    base64File !== '' ? handleFile(true) : handleFile(false);
  }, [base64File]);

  const isValidFileUploaded = (file: File) => {
    if (file) {
      if (validExtensions) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const extensionValid = fileExtension !== undefined && validExtensions.includes(fileExtension);

        // Also check MIME type for backwards compatibility
        const mimeExtension = file.type.split('/')[1];
        const mimeValid = mimeExtension !== undefined && validExtensions.includes(mimeExtension);

        return extensionValid || mimeValid;
      }
      return true;
    }
    return false;
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (disabled) {
      return;
    }

    const file = event.dataTransfer.files[0];
    const fileSize = event.dataTransfer.files[0].size;
    setHovering(false);

    if (file && isValidFileUploaded(file)) {
      let validFile = true;
      const fileIsTooLarge = maxMBSize && fileSize > maxMBSize * 1024 * 1024;
      const fileNameTooLong = file.name.length > 150;

      if (fileIsTooLarge || fileNameTooLong) {
        validFile = false;
        setIsFileValid(false);
        handleValidFile(false);

        if (fileIsTooLarge) {
          setError('maxLimit');
        } else if (fileNameTooLong) {
          setError('maxFileName');
        }
      }

      if (validFile) {
        const processedName = getProcessedFileName(file.name);
        setFileName(processedName);
        handleFileName(processedName);
        setIsFileValid(true);
        handleValidFile(true);
        setInvalidFileMessage('');
        setError(null);

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          const base64String = reader.result as string;
          const base64Content = base64String.split(',')[1];
          setBase64File(base64 ? base64String : base64Content);

          if (value !== base64Content) {
            handleHasNewFile(true);
            handleNewFile(base64String.split(',')[1]);
          }
        };
      }
    } else {
      setError('fileType');
      setIsFileValid(false);
      handleValidFile(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files !== null) {
      const fileUploaded = event.target.files[0];
      const fileSize = event.target.files[0].size;

      if (isValidFileUploaded(fileUploaded)) {
        let validFile = true;
        const fileIsTooLarge = maxMBSize && fileSize > maxMBSize * 1024 * 1024;
        const fileNameTooLong = fileUploaded.name.length > 150;

        if (fileIsTooLarge || fileNameTooLong) {
          validFile = false;
          setIsFileValid(false);
          handleValidFile(false);

          if (fileIsTooLarge) {
            setError('maxLimit');
          } else if (fileNameTooLong) {
            setError('maxFileName');
          }
        }

        if (validFile) {
          const processedName = getProcessedFileName(fileUploaded.name);
          setFileName(processedName);
          handleFileName(processedName);
          setIsFileValid(true);
          handleValidFile(true);
          setInvalidFileMessage('');
          setError(null);

          const getBase64File = async () => {
            const base64Content = (await convertBase64(event.target)) as string;
            setBase64File(base64 ? base64Content : base64Content.split(',')[1]);
            if (value !== base64Content.split(',')[1]) {
              handleHasNewFile(true);
              handleNewFile(base64Content.split(',')[1]);
            }
          };

          getBase64File();
        }
      } else {
        setError('fileType');
        setIsFileValid(false);
        handleValidFile(false);
      }
    }
  };

  const hiddenFileInput = React.useRef<HTMLInputElement>(null);

  const onClick = () => {
    if (disabled) {
      return;
    }

    if (hiddenFileInput.current !== null) {
      hiddenFileInput.current.click();
    }
  };

  useEffect(() => {
    if (hiddenFileInput && hiddenFileInput.current) hiddenFileInput.current.value = '';
  }, [base64File]);

  const openFile = () => {
    const newTab = window.open();
    let base64Data = base64File;

    try {
      if (base64Data.startsWith('data:application/pdf;base64,') || base64Data.startsWith('JVB')) {
        base64Data = base64Data.replace(/^data:application\/pdf;base64,/, '');

        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const iframeUrl = URL.createObjectURL(blob);
        newTab?.document.write(
          `<body><iframe src="${iframeUrl}" style="width: 100%; height: 100%;" frameborder="0"></iframe></body>`,
        );
      } else if (base64Data.startsWith('/9j/') || base64Data.startsWith('iVB')) {
        newTab?.document.write(`<body><img src="data:application/pdf;base64,${base64File}"/></body>`);
      } else {
        newTab?.document.write('<body>Error: Unsupported file type</body>');
      }
    } catch (e) {
      console.error('Error decoding base64 data:', e);
    }
  };

  const inputEl = useRef<HTMLInputElement>(null);
  const inputEl2 = useRef<HTMLInputElement>(null);

  const forceFireOnChange = useCallback(() => {
    /*

    Note: this is an established hack for causing React's onChange synthetic event to fire.
    https://github.com/facebook/react/issues/11488

    Typically, input's onChange function only fires from direct user input. In this case we are using react state
    to set the value of the input, so we need to fire onChange manually.

    We are doing this so that this component can generate a React.ChangeEvent for any onChange callback that is given to it.

    */

    /* eslint-disable @typescript-eslint/no-unsafe-member-access */
    /* eslint-disable @typescript-eslint/no-unsafe-call */
    /* eslint-disable no-underscore-dangle */

    interface ExtendedElement extends HTMLInputElement {
      _valueTracker: { setValue: (val: string) => void };
    }

    const currentElement = inputEl.current as ExtendedElement;
    const currentElementFileName = inputEl2.current as ExtendedElement;
    const file = fileName !== '' ? fileName : base64FileName;

    if (currentElement) {
      const lastValue = currentElement.value;

      currentElement.value = base64File;
      currentElement._valueTracker?.setValue(lastValue);
    }
    if (currentElementFileName) {
      const lastValue = currentElementFileName.value;
      currentElementFileName.value = file;
      currentElementFileName._valueTracker?.setValue(lastValue);
    }

    inputEl.current?.setAttribute('fileName', file);
    /* eslint-enable @typescript-eslint/no-unsafe-member-access */
    /* eslint-enable-line @typescript-eslint/no-unsafe-call */
    /* eslint-enable no-underscore-dangle */
    if (file !== '') {
      inputEl2.current?.dispatchEvent(new Event('change', { bubbles: true }));
      inputEl.current?.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, [base64File]);

  useEffect(() => {
    forceFireOnChange();
  }, [base64File]);

  const getFileExtension = () => {
    const cleanBase64 = base64File?.includes(',') ? base64File.split(',')[1] : base64File;

    if (!cleanBase64) return '';

    if (cleanBase64.startsWith('JVBERi')) return '.pdf';
    if (cleanBase64.startsWith('/9j/')) return '.jpeg';
    if (cleanBase64.startsWith('iVBOR')) return '.png';

    return '';
  };

  return (
    <>
      <UploadFileDialogue
        companyName={companyName || ''}
        buttonBackgroundColor={buttonBackgroundColor || ''}
        buttonTextColor={buttonTextColor || ''}
        shouldDeleteFile={shouldDeleteFile}
        language={language}
        handleShouldDeleteFile={setShouldDeleteFile}
        removeFileName={setFileName}
        removeBase64={setBase64File}
        onDeleteClick={onDeleteClick}
      />
      {base64File === '' || (base64File !== '' && invalidFileMessage !== '') ? (
        <>
          <div
            onDrop={handleDrop}
            onDragOver={(event) => event.preventDefault()}
            onDragEnter={() => {
              if (!disabled) {
                setHovering(true);
              }
            }}
            onDragLeave={() => {
              if (!disabled) {
                setHovering(false);
              }
            }}
            className={cn(
              'container',
              hovering && 'hover',
              invalidFileMessage !== '' && 'errorContainer',
              disabled && 'disabled',
            )}
            onClick={onClick}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                onClick();
              }
            }}
            role="button"
            tabIndex={disabled ? -1 : 0}
            style={{ height }}
          >
            <div className={cn('buttonContainer')}>
              <div className={cn('desc')}>
                <Icon icon="upload" color="primary-dark" width="18px" height="18px" />
                <span>
                  <Trans
                    t={t}
                    i18nKey="instructions"
                    components={{
                      underline: <span className={cn('underline')} />,
                    }}
                  />
                </span>
              </div>
              <div className={cn('maxSizeDesc')}>
                {maxMBSize &&
                  t('maxFile', {
                    size: maxMBSize,
                  })}
              </div>
            </div>
          </div>

          {invalidFileMessage !== '' && <div className={cn('errorMessage')}>{invalidFileMessage}</div>}
        </>
      ) : (
        <div className={cn('fileContainer', disabled && 'disabled')} style={{ height }}>
          <div className={cn('fileNameContainer')}>
            {deleteButton && value !== '' && (
              <button
                onClick={() => {
                  if (disabled) return;

                  setHovering(false);
                  if (isFileVaild) {
                    if (variant === 'onboarding') {
                      setShouldDeleteFile(true);
                    } else {
                      setBase64File('');
                      setFileName('');
                    }
                  } else {
                    setBase64File('');
                    setFileName('');
                  }
                }}
                type="button"
                disabled={disabled}
              >
                <Icon icon="fail" width="20px" height="20px" color="error" />
              </button>
            )}
            <span>
              {fileName}
              {isFileVaild ? getFileExtension() : ''}
            </span>

            {isFileVaild &&
              (shouldDownloadFile ? (
                <div className={cn('downloadButton')}>
                  <a href={url} download={fileName} target="_blank" rel="noreferrer">
                    <Button
                      id="download-file"
                      label={language === 'en' ? 'Download' : 'Télécharger'}
                      width="85px"
                      height="28px"
                      color="secondary"
                      textSize={variant === 'onboarding' ? 14 : 1}
                      fontUnit={variant === 'onboarding' ? 'px' : 'em'}
                      bold
                      round
                    />
                  </a>
                </div>
              ) : (
                <Button
                  id="view-file"
                  label={language === 'en' ? 'View' : 'Voir'}
                  width="50px"
                  height="28px"
                  color="secondary"
                  textSize={variant === 'onboarding' ? 14 : 1}
                  fontUnit={variant === 'onboarding' ? 'px' : 'em'}
                  bold
                  round
                  onClick={disabled ? undefined : openFile}
                  isDisabled={disabled}
                />
              ))}
            {!deleteButton && value !== '' && (
              <Button
                id="update-file-button"
                label={language === 'en' ? 'Update' : 'Mettre à jour'}
                width={language === 'en' ? '65px' : '120px'}
                height="28px"
                color="primary"
                textSize={variant === 'onboarding' ? 14 : 1}
                fontUnit={variant === 'onboarding' ? 'px' : 'em'}
                bold
                round
                onClick={disabled ? undefined : onClick}
                isDisabled={disabled}
              />
            )}
          </div>
        </div>
      )}
      <ErrorText error={error} size="sm" indentation />

      <input
        id={`UploadFileShow-${name}`}
        name={`Show-${name}`}
        type="file"
        ref={hiddenFileInput}
        className={cn('hidden')}
        maxLength={maxLength}
        onBlur={onBlur}
        onChange={handleChange}
        accept={accept}
        disabled={disabled}
      />
      <input
        id={`UploadFile-${name}`}
        name={name}
        type="text"
        className={cn('hidden')}
        ref={inputEl}
        onBlur={onBlur}
        onChange={(e) => {
          onChange(e);
        }}
        disabled={disabled}
      />
      <input
        id={`UploadFileName-${idFileName}`}
        name={idFileName}
        type="text"
        className={cn('hidden')}
        ref={inputEl2}
        onBlur={onBlurFileName}
        onChange={(e) => {
          onChangeFileName(e);
        }}
        disabled={disabled}
      />
    </>
  );
};

export default UploadFile;
